/** Web Push subscription. Payloads carry ids only; the service worker fetches details. */
import type { Api } from './api.js'

export function pushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

function b64ToBytes(b64: string) {
  const s = atob(b64.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (b64.length % 4)) % 4))
  return Uint8Array.from(s, (c) => c.charCodeAt(0))
}

export async function enablePush(
  api: Api,
  deviceId?: string,
): Promise<'enabled' | 'denied' | 'unsupported' | 'unavailable'> {
  if (!pushSupported()) return 'unsupported'
  const perm = await Notification.requestPermission()
  if (perm !== 'granted') return 'denied'
  let key: string
  try {
    key = (await api.vapidKey()).publicKey
  } catch {
    return 'unavailable'
  }
  const reg = await navigator.serviceWorker.ready
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: b64ToBytes(key),
    }))
  await api.pushSubscribe(sub.toJSON(), deviceId)
  return 'enabled'
}

export async function disablePush(api: Api) {
  if (!pushSupported()) return
  const reg = await navigator.serviceWorker.getRegistration()
  const sub = await reg?.pushManager.getSubscription()
  if (!sub) return
  await api.pushUnsubscribe(sub.endpoint).catch(() => {})
  await sub.unsubscribe()
}
